import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { TruckIcon, StoreIcon, WrenchIcon, ArrowRightIcon, DownloadIcon, CheckCircleIcon } from 'lucide-react';
type Language = 'ua' | 'en';
type PartnerCTAProps = {
  language: Language;
};
const partners = {
  ua: [{
    icon: TruckIcon,
    title: "Дистриб'ютори / локальні оптовики",
    description: "Станьте офіційним дистриб'ютором CHAMALEON у вашому регіоні та отримайте ексклюзивні умови співпраці"
  }, {
    icon: StoreIcon,
    title: 'Онлайн / офлайн ритейлери',
    description: 'Розширте асортимент вашого магазину преміальною продукцією з підтримкою маркетингу'
  }, {
    icon: WrenchIcon,
    title: 'Кузовні сервіси (Body Shops)',
    description: 'Професійні рішення для автосервісів з технічною підтримкою та навчанням персоналу'
  }],
  en: [{
    icon: TruckIcon,
    title: 'Distributors / Local Wholesalers',
    description: 'Become an official CHAMALEON distributor in your region and get exclusive partnership terms'
  }, {
    icon: StoreIcon,
    title: 'Online / Offline Retailers',
    description: "Expand your store's range with premium products and marketing support"
  }, {
    icon: WrenchIcon,
    title: 'Body Shops',
    description: 'Professional solutions for auto services with technical support and staff training'
  }]
};
const benefits = {
  ua: ['Ексклюзивні умови', 'Маркетингова підтримка', 'Технічне навчання', 'Швидка логістика'],
  en: ['Exclusive terms', 'Marketing support', 'Technical training', 'Fast logistics']
};
const sectionText = {
  ua: {
    title: 'Станьте партнером CHAMALEON',
    subtitle: 'Приєднуйтесь до мережі успішних партнерів у 80 країнах світу',
    cta: 'Подати заявку на партнерство',
    download: 'Завантажити презентацію бренду'
  },
  en: {
    title: 'Become a CHAMALEON Partner',
    subtitle: 'Join the network of successful partners in 80 countries worldwide',
    cta: 'Apply for Partnership',
    download: 'Download Brand Presentation'
  }
};
export function PartnerCTA({
  language
}: PartnerCTAProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const t = sectionText[language];
  const partnerList = partners[language];
  const benefitList = benefits[language];
  return <section ref={ref} id="partner" className="relative py-16 sm:py-20 lg:py-32 bg-chamaleon-black overflow-hidden" data-id="element-251">
      {/* Background */}
      <div className="absolute inset-0" data-id="element-252">
        <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `repeating-linear-gradient(
            45deg,
            rgba(255,107,0,0.1),
            rgba(255,107,0,0.1) 2px,
            transparent 2px,
            transparent 20px
          )`
      }} data-id="element-253" />

        <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[1000px] rounded-full opacity-[0.08]" style={{
        background: 'radial-gradient(circle, #FF6B00 0%, transparent 60%)',
        filter: 'blur(120px)'
      }} animate={{
        scale: [1, 1.1, 1],
        opacity: [0.08, 0.12, 0.08]
      }} transition={{
        duration: 8,
        repeat: Infinity
      }} data-id="element-254" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-255">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-8 sm:mb-12" data-id="element-256">
          <div className="inline-flex items-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-chamaleon-orange/20 border-2 border-chamaleon-orange/40 rounded-full mb-4 sm:mb-6" data-id="element-257">
            <div className="w-2 h-2 rounded-full bg-chamaleon-orange animate-pulse" data-id="element-258" />
            <span className="text-chamaleon-orange font-semibold tracking-wide uppercase text-xs sm:text-sm" data-id="element-259">
              {language === 'ua' ? 'Партнерство' : 'Partnership'}
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 px-4" data-id="element-260">
            {t.title}
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto px-4" data-id="element-261">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Benefits badges - Desktop only */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="hidden lg:flex flex-wrap justify-center gap-3 mb-12" data-id="element-262">
          {benefitList.map(benefit => <div key={benefit} className="flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full" data-id="element-263">
              <CheckCircleIcon className="w-4 h-4 text-chamaleon-orange" data-id="element-264" />
              <span className="text-sm text-gray-300" data-id="element-265">{benefit}</span>
            </div>)}
        </motion.div>

        {/* Partner Cards - Unified style with Bestsellers */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12" data-id="element-266">
          {partnerList.map((partner, index) => {
          const Icon = partner.icon;
          return <motion.div key={partner.title} initial={{
            opacity: 0,
            y: 30
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6,
            delay: 0.3 + index * 0.1
          }} whileHover={{
            y: -8
          }} className="group" data-id="element-267">
                <div className="glass-card rounded-xl sm:rounded-2xl p-6 sm:p-8 h-full transition-all duration-300 hover:border-chamaleon-orange/30" data-id="element-268">
                  {/* Icon */}
                  <div className="relative mb-4 sm:mb-6 w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center" data-id="element-269">
                    <div className="absolute inset-0 bg-gradient-to-br from-chamaleon-orange/20 to-chamaleon-orange/5 rounded-xl group-hover:from-chamaleon-orange/30 group-hover:to-chamaleon-orange/10 transition-all" data-id="element-270" />
                    <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-chamaleon-orange relative z-10" data-id="element-271" />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4 group-hover:text-chamaleon-orange transition-colors" data-id="element-272">
                    {partner.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm sm:text-base text-gray-400 leading-relaxed" data-id="element-273">
                    {partner.description}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>

        {/* CTAs */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 px-4" data-id="element-274">
          <motion.a href="#" className="group relative inline-flex items-center justify-center gap-2 px-8 sm:px-10 py-4 sm:py-5 bg-chamaleon-orange text-white font-bold rounded-xl overflow-hidden text-base sm:text-lg shadow-glow-orange" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-275">
            <span className="relative z-10" data-id="element-276">{t.cta}</span>
            <ArrowRightIcon className="relative z-10 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" data-id="element-277" />
            <div className="absolute inset-0 bg-gradient-to-r from-chamaleon-orange-dark to-chamaleon-orange opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-278" />
          </motion.a>

          <motion.a href="#" className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-4 sm:py-5 bg-white/5 backdrop-blur-sm border-2 border-white/10 text-white font-medium rounded-xl hover:bg-white/10 hover:border-chamaleon-orange/30 transition-all text-sm sm:text-base" whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} data-id="element-279">
            <DownloadIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-280" />
            <span className="hidden sm:inline" data-id="element-281">{t.download}</span>
            <span className="sm:hidden" data-id="element-282">
              {language === 'ua' ? 'Презентація' : 'Presentation'}
            </span>
          </motion.a>
        </motion.div>
      </div>
    </section>;
}